import { useEffect, useState, type ReactNode } from 'react'
import type { Stage } from '@one-deployment/shared'
import { AlertTriangle, Check, Copy as CopyIcon, Inbox, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/cn'
import { age } from '@/lib/format'
import { ApiError } from '@/lib/api'
import { STAGE_LOOK } from './stage'
import { Button } from './ui'

export function StatusDot({ stage, printing, className }: { stage: Stage; printing?: boolean; className?: string }) {
  const look = STAGE_LOOK[stage]
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block size-2 shrink-0 rounded-full',
        look.hollow ? `border-[1.5px] bg-transparent ${look.text} border-current` : look.dot,
        printing && 'pulse-dot',
        className,
      )}
    />
  )
}

export function StageBadge({ stage, printing }: { stage: Stage; printing?: boolean }) {
  const look = STAGE_LOOK[stage]
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs', look.text)}>
      <StatusDot stage={stage} printing={printing} />
      {look.label}
    </span>
  )
}

/** Live "14m": re-renders itself every few seconds; nothing else moves on data updates. */
export function Age({ from, className, prefix }: { from: number | null | undefined; className?: string; prefix?: string }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 5_000)
    return () => clearInterval(t)
  }, [])
  if (!from) return <span className={className}>—</span>
  return (
    <span className={cn('tabular-nums', className)} title={new Date(from).toLocaleString()}>
      {prefix}
      {age(from)}
    </span>
  )
}

/** An empty state is an instruction, not a blank (03 §8). */
export function EmptyState({ icon, title, body, action }: { icon?: ReactNode; title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <div className="text-fg-faint">{icon ?? <Inbox size={20} />}</div>
      <div className="text-sm font-medium text-fg">{title}</div>
      {body && <div className="max-w-md text-xs text-fg-subtle">{body}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

/**
 * The API's own `what` and `todo` when it gave them, so the reader is told
 * what to do rather than handed an error code.
 */
export function ErrorState({ error, retry, compact }: { error: unknown; retry?: () => void; compact?: boolean }) {
  const e = error instanceof ApiError ? error : null
  let what = e?.what ?? (error instanceof Error ? error.message : 'Something went wrong.')
  let todo = e?.todo ?? 'Try again. If it keeps failing, check Alerts.'
  if (e?.status === 403) {
    what = "You don't have access to this."
    todo = 'Ask an admin to grant you a role on this project.'
  } else if (e?.status === 404) {
    what = 'Not found — it may have been archived.'
    todo = 'Go back, or open it from the projects page.'
  }
  return (
    <div className={cn('flex items-start gap-3 rounded-md border border-err/30 bg-err/5 text-sm', compact ? 'p-2.5' : 'm-4 p-4')} role="alert">
      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-err" />
      <div className="min-w-0 flex-1">
        <div className="text-fg">{what}</div>
        <div className="mt-0.5 text-xs text-fg-subtle">{todo}</div>
      </div>
      {retry && e?.status !== 403 && e?.status !== 404 && (
        <Button size="sm" onClick={retry}>
          <RefreshCw size={12} /> Retry
        </Button>
      )}
    </div>
  )
}

export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false)
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setDone(true)
          setTimeout(() => setDone(false), 1500)
        })
      }}
    >
      {done ? <Check size={12} /> : <CopyIcon size={12} />} {done ? 'Copied' : label}
    </Button>
  )
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('font-mono text-xs', className)}>{children}</span>
}
